import { useCallback } from 'react';
import { listApiKeys, createApiKey, rotateApiKey, revokeApiKey } from '../lib/apiClient';
import { useDataFetching } from './useDataFetching';
import type {
  ApiKeyMeta,
  CreateApiKeyInput,
  CreateApiKeyResponse,
  RotateApiKeyResponse,
} from '../types/apiKeys';

interface UseApiKeysResult {
  keys: ApiKeyMeta[];
  loading: boolean;
  error: string | null;
  createKey: (input: CreateApiKeyInput) => Promise<CreateApiKeyResponse>;
  rotateKey: (keyId: string) => Promise<RotateApiKeyResponse>;
  revokeKey: (keyId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useApiKeys(): UseApiKeysResult {
  const { items, setItems, loading, error, refetch, create, remove } = useDataFetching<
    ApiKeyMeta,
    CreateApiKeyInput,
    CreateApiKeyResponse
  >({
    fetchFn: listApiKeys,
    createFn: createApiKey,
    selectCreated: (response) => response.key,
    deleteFn: revokeApiKey,
    getItemId: (key) => key.keyId,
  });

  const rotateKey = useCallback(
    async (keyId: string): Promise<RotateApiKeyResponse> => {
      const response = await rotateApiKey(keyId);
      setItems((prev) =>
        prev
          .map((k) => (k.keyId === keyId ? { ...k, status: 'Rotating' as const } : k))
          .concat(response.key)
      );
      return response;
    },
    [setItems]
  );

  return {
    keys: items,
    loading,
    error,
    createKey: create,
    rotateKey,
    revokeKey: remove,
    refetch,
  };
}
